import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  addDays,
  addWeeks,
  endOfWeek,
  format,
  isSameMonth,
  startOfWeek,
} from "date-fns";
import { nl } from "date-fns/locale";
import {
  CaretLeft,
  CaretRight,
  CalendarBlank,
  ArrowsClockwise,
} from "@phosphor-icons/react";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { WeekGrid } from "./WeekGrid";
import { EventDetailDialog } from "./EventDetailDialog";
import { OWNER_STYLES, SharedCalendarEvent } from "./types";

export const SharedCalendarPage: React.FC = () => {
  const { session } = useSession();
  const [anchorDate, setAnchorDate] = useState<Date>(new Date());
  const [events, setEvents] = useState<SharedCalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<SharedCalendarEvent | null>(
    null,
  );

  const weekStart = useMemo(
    () => startOfWeek(anchorDate, { weekStartsOn: 1 }),
    [anchorDate],
  );
  const weekEnd = useMemo(
    () => endOfWeek(anchorDate, { weekStartsOn: 1 }),
    [anchorDate],
  );

  const headerLabel = useMemo(() => {
    const sameMonth = isSameMonth(weekStart, weekEnd);
    if (sameMonth) {
      return `${format(weekStart, "d", { locale: nl })} – ${format(
        weekEnd,
        "d MMMM yyyy",
        { locale: nl },
      )}`;
    }
    return `${format(weekStart, "d MMM", { locale: nl })} – ${format(
      weekEnd,
      "d MMM yyyy",
      { locale: nl },
    )}`;
  }, [weekStart, weekEnd]);

  const fetchEvents = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    try {
      const startISO = weekStart.toISOString();
      // Fetch up to next-day midnight to include events ending on Sunday
      const endISO = addDays(weekEnd, 1).toISOString();
      const response = await fetchWithAuth(
        `${API_URL}/shared-calendar/events?start=${encodeURIComponent(
          startISO,
        )}&end=${encodeURIComponent(endISO)}`,
        { method: "GET" },
        session,
      );
      if (!response.ok) {
        throw new Error(`Failed: ${response.status}`);
      }
      const data = await response.json();
      setEvents(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("[SHARED-CALENDAR] Fetch failed:", error);
      toast.error("Kon agenda niet laden");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [session, weekStart, weekEnd]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const goPrev = () => setAnchorDate((d) => addWeeks(d, -1));
  const goNext = () => setAnchorDate((d) => addWeeks(d, 1));
  const goToday = () => setAnchorDate(new Date());

  return (
    <div className="w-full px-2 py-4 sm:px-4 lg:px-6">
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <CalendarBlank
            size={28}
            weight="duotone"
            className="text-moss-primary"
          />
          <div>
            <h1 className="text-neutral-primary text-xl font-semibold">
              Agenda
            </h1>
            <p className="text-neutral-secondary-mid text-xs">
              Gedeelde weekkalender — Ella plant verhuur afspraken, Leo plant
              onderhoud afspraken.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Legend */}
          <div className="border-border mr-3 flex items-center gap-4 rounded-md border bg-white px-3 py-1.5">
            {(["ella", "leo"] as const).map((owner) => (
              <div key={owner} className="flex items-center gap-2">
                <span
                  className="inline-block h-3 w-3 rounded-full"
                  style={{ backgroundColor: OWNER_STYLES[owner].accentHex }}
                  aria-hidden="true"
                />
                <div className="leading-tight">
                  <div className="text-neutral-primary text-xs font-semibold">
                    {OWNER_STYLES[owner].label}
                  </div>
                  <div className="text-neutral-secondary-mid text-[10px]">
                    {OWNER_STYLES[owner].description}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Button variant="outline" size="sm" onClick={goToday}>
            Vandaag
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={goPrev}
            aria-label="Vorige week"
          >
            <CaretLeft size={18} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={goNext}
            aria-label="Volgende week"
          >
            <CaretRight size={18} />
          </Button>
          <span className="text-neutral-primary ml-2 text-sm font-medium">
            {headerLabel}
          </span>
          <Button
            variant="ghost"
            size="icon"
            onClick={fetchEvents}
            aria-label="Vernieuwen"
            disabled={loading}
          >
            <ArrowsClockwise
              size={18}
              className={loading ? "animate-spin" : ""}
            />
          </Button>
        </div>
      </div>

      {/* Loading overlay */}
      {loading && events.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      ) : (
        <WeekGrid
          weekStart={weekStart}
          events={events}
          onEventClick={setSelectedEvent}
        />
      )}

      <EventDetailDialog
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
      />
    </div>
  );
};

export default SharedCalendarPage;
